// =====================================================================
// Favorites store — same persistence strategy as cart.
// =====================================================================

import { create } from "zustand";
import { FavoriteRefSchema, type FavoriteRef } from "@/shared/lib/db-schema";
import { loadFavorites, saveFavorites } from "./storage";

interface FavoritesState {
  items: ReadonlyArray<FavoriteRef>;
  keySet: ReadonlySet<string>;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  toggle: (productId: string, color: string) => Promise<boolean>;
  isFavorite: (productId: string, color: string) => boolean;
  clear: () => Promise<void>;
}

function buildKey(productId: string, color: string): string {
  return `${productId}:${color}`;
}

function buildSet(items: ReadonlyArray<FavoriteRef>): Set<string> {
  const s = new Set<string>();
  for (const f of items) s.add(buildKey(f.productId, f.color));
  return s;
}

export const useFavoritesStore = create<FavoritesState>()((set, get) => ({
  items: [],
  keySet: new Set<string>(),
  hydrated: false,

  hydrate: async () => {
    if (get().hydrated) return;
    const loaded = await loadFavorites();
    const parsed: FavoriteRef[] = [];
    for (const row of loaded) {
      const r = FavoriteRefSchema.safeParse(row);
      if (r.success) parsed.push(r.data);
    }
    set({ items: parsed, keySet: buildSet(parsed), hydrated: true });
  },

  toggle: async (productId, color) => {
    const items = get().items;
    const key = buildKey(productId, color);
    if (get().isFavorite(productId, color)) {
      const next = items.filter((f) => buildKey(f.productId, f.color) !== key);
      set({ items: next, keySet: buildSet(next) });
      await saveFavorites([...next]);
      return false;
    }
    const next: ReadonlyArray<FavoriteRef> = [
      ...items,
      { productId, color, addedAt: Date.now() },
    ];
    set({ items: next, keySet: buildSet(next) });
    await saveFavorites([...next]);
    return true;
  },

  isFavorite: (productId, color) =>
    get().keySet.has(buildKey(productId, color)),

  clear: async () => {
    set({ items: [], keySet: new Set<string>() });
    await saveFavorites([]);
  },
}));

export const useFavorites = () => useFavoritesStore((s) => s.items);
export const useFavoritesHydrated = () => useFavoritesStore((s) => s.hydrated);
export const useFavoritesToggle = () => useFavoritesStore((s) => s.toggle);
export const useFavoritesClear = () => useFavoritesStore((s) => s.clear);
export const useFavoritesCount = (): number => useFavoritesStore((s) => s.items.length);

export const useIsFavorite = (productId: string, color: string): boolean =>
  useFavoritesStore((s) => s.keySet.has(buildKey(productId, color)));
